import { isAxiosError } from 'axios';
import {
  ConfirmUserRegistrationError,
  ResendUserRegistrationError,
  UserRegistrationError,
  type UserRegistrationRequest,
} from '@/services/register.types';

export type SubmitValues = {
  email: string;
  password: string;
  phoneNumber_dialCode?: string;
  phoneNumber_base?: string;
  phoneNumber_countryCode?: string;
};

export const buildRegistrationRequest = (
  values: SubmitValues,
  registrationToken: string,
): UserRegistrationRequest => ({
  email: values.email,
  password: values.password,
  phoneNumber: values.phoneNumber_base,
  phoneCallingCode: values.phoneNumber_dialCode,
  registrationToken,
});

/**
 * Business error returned by registrationConfirm inside a 2xx response body.
 * Thrown from the mutation so both transport and business failures share the
 * same error path.
 */
export class ConfirmRegistrationError extends Error {
  readonly code: ConfirmUserRegistrationError;

  constructor(code: ConfirmUserRegistrationError) {
    super(code);
    this.name = 'ConfirmRegistrationError';
    this.code = code;
  }
}

const isKnownCode = <T extends Record<string, string>>(
  codes: T,
  code: string | undefined,
): code is T[keyof T] => !!code && Object.values(codes).includes(code);

/**
 * Error code carried by a failed response, whichever field the endpoint uses.
 */
const readResponseErrorCode = (error: unknown): string | undefined => {
  if (isAxiosError<{ error?: string; apiErrorCode?: string }>(error)) {
    const { error: errorCode, apiErrorCode } = error.response?.data ?? {};

    return errorCode ?? apiErrorCode;
  }

  return undefined;
};

export const resolveRegisterErrorCode = (
  error: unknown,
): UserRegistrationError => {
  const responseErrorCode = readResponseErrorCode(error);

  return isKnownCode(UserRegistrationError, responseErrorCode)
    ? responseErrorCode
    : UserRegistrationError.INTERNAL_SERVER_ERROR;
};

export const resolveConfirmErrorCode = (
  error: unknown,
): ConfirmUserRegistrationError => {
  if (error instanceof ConfirmRegistrationError) {
    return error.code;
  }

  // The backend may also report the same enum alongside a non-2xx status.
  const responseErrorCode = readResponseErrorCode(error);

  return isKnownCode(ConfirmUserRegistrationError, responseErrorCode)
    ? responseErrorCode
    : ConfirmUserRegistrationError.INTERNAL_SERVER_ERROR;
};

/**
 * Resend has no catch-all enum member — an unrecognised failure resolves to
 * undefined so the caller falls back to its generic message.
 */
export const resolveResendErrorCode = (
  error: unknown,
): ResendUserRegistrationError | undefined => {
  const responseErrorCode = readResponseErrorCode(error);

  return isKnownCode(ResendUserRegistrationError, responseErrorCode)
    ? responseErrorCode
    : undefined;
};
